import React, { useState, useRef, useEffect, useCallback } from "react";
import { Row, Col, Carousel, Modal } from "antd";
import { CameraOutlined, CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { bucket_base_url, FALLBACK_IMAGE } from "../../../constants/constant";
import { imageCache } from "../../../utils/constant";

const ImageCarousel = ({ images = [] }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef(null);

  const getImageSrc = (image) => {
    if (!image?.filePath) return FALLBACK_IMAGE;
    return image.filePath.includes("http")
      ? image.filePath
      : `${bucket_base_url}${image.filePath}`;
  };

  // Preload all images into cache immediately on mount so they display without flicker
  useEffect(() => {
    if (!images || images.length === 0) return;
    images.forEach((image) => {
      const url = getImageSrc(image);
      if (url === FALLBACK_IMAGE) return;
      if (!imageCache.has(url)) {
        const img = new window.Image();
        img.src = url;
        img.onload = () => imageCache.set(url, true);
        img.onerror = () => imageCache.set(url, true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  const handleOpenModal = (index) => {
    if (images.length === 0) return;
    setCurrentSlide(index);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleThumbnailClick = (index) => {
    setCurrentSlide(index);
    carouselRef.current.goTo(index);
  };

  const goToPreviousSlide = useCallback(() => {
    if (carouselRef.current) {
      carouselRef.current.prev();
      setCurrentSlide((prevSlide) => (prevSlide > 0 ? prevSlide - 1 : (images.length || 1) - 1));
    }
  }, [carouselRef, images.length]);

  const goToNextSlide = useCallback(() => {
    if (carouselRef.current) {
      carouselRef.current.next();
      setCurrentSlide((prevSlide) => (prevSlide + 1) % (images.length || 1));
    }
  }, [carouselRef, images.length]);

  useEffect(() => {
    if (isModalVisible) {
      const handleKeyDown = (event) => {
        if (event.key === "ArrowLeft") {
          goToPreviousSlide();
        } else if (event.key === "ArrowRight") {
          goToNextSlide();
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isModalVisible, goToPreviousSlide, goToNextSlide]);

  useEffect(() => {
    if (isModalVisible && carouselRef.current) {
      carouselRef.current.goTo(currentSlide);
    }
  }, [isModalVisible, currentSlide]);

  return (
    <>
      <div className={`carousel-container ${images.length === 0 ? "no-images" : ""}`}>
        <Row gutter={16} style={{ display: "flex", justifyContent: "center" }}>
          <Col xs={24} sm={images.length > 1 ? 16 : 24} style={{ position: "relative" }}>
            <img
              className={`carousel-image-large ${images.length === 0 ? "is-fallback" : ""}`}
              src={getImageSrc(images[0])}
              alt="Property large"
              onClick={() => handleOpenModal(0)}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = FALLBACK_IMAGE;
              }}
              style={{ width: '100%', objectFit: 'cover' }}
            />
            {images?.length > 0 && (
              <div className="image-count" onClick={() => handleOpenModal(0)}>
                <CameraOutlined style={{ fontSize: '24px', color: 'white' }} />
                <span>{images?.length} Photos</span>
              </div>
            )}
          </Col>
          {images.length > 1 && (
            <Col xs={24} sm={8}>
              <Row gutter={[0, 16]}>
                {images.slice(1, 3).map((image, index) => (
                  <Col key={index} span={24}>
                    <img
                      className="carousel-image-small"
                      src={getImageSrc(image)}
                      alt={`Property small ${index + 1}`}
                      onClick={() => handleOpenModal(index + 1)}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = FALLBACK_IMAGE;
                      }}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          )}
        </Row>
      </div>

      <Modal
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width="100vw"
        height="100vh"
        zIndex={10001}
        bodyStyle={{ padding: 0, backgroundColor: 'black', overflow: 'hidden' }}
        className="image-gallery-modal"
        style={{
          top: 0,
          overflow: 'hidden',
          maxWidth: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
        closeIcon={<CloseOutlined className="close-icon" />}
      >
        <div className="modal-content-wrapper" style={{ overflow: 'hidden' }}>
          <div className="carousel-navigation">
            <LeftOutlined onClick={goToPreviousSlide} className="carousel-arrow" style={{ color: 'white', fontSize: '24px', position: 'absolute', left: '10px', top: '50%' }} />
            <RightOutlined onClick={goToNextSlide} className="carousel-arrow" style={{ color: 'white', fontSize: '24px', position: 'absolute', right: '10px', top: '50%', marginRight: '30px' }} />
          </div>

          <Carousel
            ref={carouselRef}
            dots={false}
            autoplay={false}
            infinite={images.length > 1}
            className="carousel-fullscreen"
            arrows
            swipeToSlide={true}
            draggable={true}
            touchMove={true}
          >
            {images.length > 0 ? (
              images.map((image, index) => (
                <div key={index} className="fullscreen-slide">
                  <img
                    className="modal-image"
                    src={getImageSrc(image)}
                    alt={`Property ${index}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = FALLBACK_IMAGE;
                    }}
                  />
                </div>
              ))
            ) : (
              <div key="fallback" className="fullscreen-slide">
                <img
                  className="modal-image"
                  src={FALLBACK_IMAGE}
                  alt="No image available"
                />
              </div>
            )}
          </Carousel>

          <div className="thumbnails">
            {images.map((image, index) => (
              <div key={index} className="thumbnail-wrapper">
                {index === currentSlide && (
                  <LeftOutlined onClick={goToPreviousSlide} className="thumbnail-arrow" style={{ color: 'white', fontSize: '18px', position: 'absolute', left: '-30px', top: '50%' }} />
                )}
                <img
                  className={`thumbnail ${currentSlide === index ? 'active' : ''}`}
                  src={getImageSrc(image)}
                  alt={`Thumbnail ${index}`}
                  onClick={() => handleThumbnailClick(index)}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = FALLBACK_IMAGE;
                  }}
                />
                {index === currentSlide && (
                  <RightOutlined onClick={goToNextSlide} className="thumbnail-arrow" style={{ color: 'white', fontSize: '18px', position: 'absolute', right: '-20px', top: '50%' }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ImageCarousel;
